import { type SetOptional } from '@frontend/duck-tape';
import type { MakeInputProps, SelectOption } from '@frontend/forms';
import type { ClassName } from '@frontend/web-utils';
import { concatClasses, safeCss } from '@frontend/web-utils';
import type { AutocompleteProps as MAutocompleteProps } from '@mantine/core';
import { Autocomplete, useCombobox } from '@mantine/core';
import type { AccessibilityLabelProps } from '../../../../../types/accessibility';
import { XStack } from '../../../Containers';
import { Icon } from '../../../Icon/Icon';
import { Emoji } from '../../../Texts';
import type { EmojiShortcode } from '../../../Texts/Emoji/emojiData';
import { Text } from '../../../Texts/Text';
import type { InputLabelProps } from '../../InputLabel';
import { InputLabel } from '../../InputLabel';

type AutocompleteInputProps<
  TValue extends string,
  TOption extends SelectOption<TValue, never, { emojiName?: EmojiShortcode; label: string }>,
> = MakeInputProps<
  TOption['value'],
  AccessibilityLabelProps &
    Pick<MAutocompleteProps, 'autoComplete' | 'autoFocus' | 'onOptionSubmit' | 'onSelect'> & {
      className?: ClassName;
      data: TOption[];
      hideRightArrow?: boolean;
      labelProps?: SetOptional<InputLabelProps, 'label'>;
      name: string;
      placeholder?: string;
    }
>;

export const AutocompleteInput = <
  TValue extends string,
  TOption extends SelectOption<TValue, never, { emojiName?: EmojiShortcode; label: string }>,
>({
  accessibilityLabel,
  className,
  data,
  error,
  hideRightArrow,
  label,
  labelProps,
  name,
  onBlur,
  onChange,
  placeholder = 'Select an option',
  showOptionalText,
  value,
  ...props
}: AutocompleteInputProps<TValue, TOption>) => {
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  const classNames: MAutocompleteProps['classNames'] = {
    input: concatClasses(safeCss('p-md text-p-md h-[56px] rounded-sm focus-within:border-textPrimary'), className),
    option: safeCss('p-none data-[combobox-selected]:bg-neutral-100'),
    root: concatClasses('flex flex-col gap-y-sm', className),
  };

  return (
    <Autocomplete
      aria-label={accessibilityLabel || label}
      classNames={classNames}
      data={data}
      error={error}
      label={label ? <InputLabel label={label} showOptionalText={showOptionalText} {...labelProps} /> : null}
      maxDropdownHeight={300}
      name={name}
      onBlur={onBlur}
      onChange={(value) => onChange(value as SelectOption<TValue>['value'])}
      placeholder={placeholder}
      renderOption={(item) => {
        const userProvidedOption = data.find((option) => option.value === item.option.value);
        return (
          <XStack className="p-md  gap-x-xs items-center" key={item.option.value}>
            {userProvidedOption?.emojiName ? <Emoji emoji={userProvidedOption?.emojiName} /> : null}
            <Text>{userProvidedOption?.label}</Text>
          </XStack>
        );
      }}
      rightSection={hideRightArrow ? null : <Icon name="IconChevronDown" />}
      role="combobox"
      value={value}
      {...props}
    />
  );
};
